import { useCallback, useState } from "react";
import toast from "react-hot-toast";

interface UseReorderModeOptions<T> {
  /** Fetch the complete list in its current saved order (unpaginated). */
  fetchAll: () => Promise<T[]>;
  /** Persist the new order. Receives the items in their working-copy order. */
  saveOrder: (items: T[]) => Promise<void>;
  /** Called after a successful save (e.g. refetch the paginated view). */
  onSaved?: () => void;
}

/**
 * Reorder-mode state machine for paginated lists with a saved custom order.
 *
 * The mode owns its own full, unpaginated working copy, so entering it never
 * touches the pagination state or the URL: `start()` fetches everything,
 * drags mutate the local copy via `reorder`, and `save()`/`cancel()` exit.
 */
export function useReorderMode<T>({ fetchAll, saveOrder, onSaved }: UseReorderModeOptions<T>) {
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<T[]>([]);

  const start = useCallback(async () => {
    setActive(true);
    setLoading(true);
    try {
      setItems(await fetchAll());
    } catch (error) {
      console.error("Error loading items to reorder:", error);
      toast.error("Failed to load items for reordering. Try again.");
      setActive(false);
    } finally {
      setLoading(false);
    }
  }, [fetchAll]);

  const cancel = useCallback(() => {
    setActive(false);
    setItems([]);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await saveOrder(items);
      toast.success("Order saved successfully!");
      setActive(false);
      setItems([]);
      onSaved?.();
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error("Failed to save order. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [items, saveOrder, onSaved]);

  return { active, loading, saving, items, reorder: setItems, start, cancel, save };
}
